// Customer app — booking flow (book/[id]) and the pass (pass/[code]) strings. The design
// handoff chrome lives in app-strings.ts (`app.booking.*`); these are the parameterised
// replacements for its demo copy (named businesses, prices, dates) plus the states the
// real API needs (errors, OTP outcomes, payment proof, offline pass). `{brand}` is the
// app name from config (INF-1); wallet names are data labels, not tenant brands.
export const appEnBook = {
  'app.book.title': 'Book',
  'app.book.closeA11y': 'Close and leave booking',
  'app.book.optionalSuffix': '{label} (optional)',
  'app.book.decrease': 'Decrease {label}',
  'app.book.increase': 'Increase {label}',
  'app.book.num': '{n}',
  'app.book.backA11y': 'Back',
  'app.book.chooseServices': 'Choose services',
  'app.book.maxPer': 'Max {n} per booking',
  'app.book.attendeeName': 'Name of the person attending',
  'app.book.noteFor': 'Booked for {name}',
  'app.book.pickStart': 'Pick your start date',
  'app.book.pickRange': 'Pick pickup and return dates',
  'app.book.pickSessions': 'Pick your sessions',
  'app.book.peakLine': 'Peak {range} +{amount}',
  'app.book.hoursSummary': '{n, plural, one{# hour} other{# hours}} · {range}',
  'app.book.hoursNotBack': '{n, plural, one{# hour} other{# hours}} · not back-to-back',
  'app.book.bookedOf': '{booked} of {capacity} booked',
  'app.book.serialsLeft': '{n, plural, one{# serial left} other{# serials left}}',
  'app.book.serialNote': 'Your place in the queue. We hold it while you finish booking.',
  'app.book.nights': '{n, plural, one{# night} other{# nights}}',
  'app.book.days': '{n, plural, one{# day} other{# days}}',
  'app.book.prevMonth': 'Previous month',
  'app.book.nextMonth': 'Next month',
  'app.book.priceTimes': '{label} · {price} × {qty}',
  'app.book.nextFree': 'Next free: {date}',
  'app.book.noTimesAny': 'No times are open right now. Please call the business to book.',
  'app.book.timesError': 'We couldn’t load the times.',
  'app.book.pageError': 'We couldn’t load this business.',
  'app.book.notFound': 'We can’t find this business.',
  'app.book.holdBusy': 'Holding this time…',
  'app.book.discount': 'Offer discount',
  'app.book.payNowPct': 'Pay now ({pct}% advance)',
  'app.book.otpSentTo': 'Enter the 6-digit code sent to {phone}',
  'app.book.otpWrong': 'That code didn’t work. Check it and try again.',
  'app.book.otpLocked': 'Too many wrong tries. Try again in 10 minutes.',
  'app.book.otpRateLimited': 'Too many codes sent. Try again in a few minutes.',
  'app.book.otpSendFailed': 'We couldn’t send the code. Try again.',
  'app.book.otpVerifyFailed': 'We couldn’t verify the code. Try again.',
  'app.book.resendCode': 'Resend code',
  'app.book.phoneA11y': 'Phone number',
  'app.book.payDirectSub': '{wallets} · the business verifies it',
  'app.book.listOr': 'or',
  'app.book.payVenueSub': 'Full {amount} at the venue',
  'app.book.payVenueSubRisk': 'Full {amount} at the venue · slot not guaranteed',
  'app.book.payVenueFree': 'Nothing to pay now · pay at the venue',
  'app.book.wallet.bkash': 'bKash',
  'app.book.wallet.nagad': 'Nagad',
  'app.book.wallet.rocket': 'Rocket',
  'app.book.wallet.bank': 'Bank transfer',
  'app.book.sendAmt': 'Send {amount} ({pct}% advance)',
  'app.book.sendAmtPlain': 'Send {amount}',
  'app.book.stepOpenWallet': 'Open {wallet} → Send Money',
  'app.book.stepOpenBank': 'Open your banking app → Transfer',
  'app.book.stepSend': 'Send {amount} to {number}',
  'app.book.openWallet': 'Open {wallet}',
  'app.book.openWalletFailed': 'Couldn’t open {wallet}. Open the app yourself and send the money.',
  'app.book.maskedNote': 'The full number is shown right after you confirm. Send the money then, and add your payment proof from the pass.',
  'app.book.passSendTo': 'Send {amount} to {number}, then send your payment proof so the business can confirm.',
  'app.book.passSendProof': 'Send {amount} to the business, then send your payment proof so they can confirm.',
  'app.book.helper': 'The TrxID or a screenshot is enough. The business checks it within 2 hours.',
  'app.book.shotAdded': 'Screenshot added',
  'app.book.removeShot': 'Remove',
  'app.book.shotTitle': 'Add a screenshot',
  'app.book.camera': 'Take a photo',
  'app.book.gallery': 'Choose from gallery',
  'app.book.shotDenied': 'Allow photo access in Settings to add a screenshot.',
  'app.book.shotFailed': 'We couldn’t read that photo. Try another one.',
  'app.book.confirming': 'Confirming…',
  'app.book.callToBookT': 'Call to book',
  'app.book.callToBookB': 'This business can’t take payment details online. Please call or message them to book.',
  'app.book.errGeneric': 'Something went wrong. Try again.',
  'app.book.errOffline': 'You’re offline. Reconnect to continue.',
  'app.book.errAuth': 'Please verify your phone again.',
  'app.book.errStale': 'This service changed. Go back and check your details.',
  'app.book.hintPhone': 'Verify your phone to confirm.',
  'app.book.hintName': 'Add your name to confirm.',
  'app.book.hintPay': 'Choose how you’ll pay.',
  'app.book.hintProof': 'Add a TrxID or a screenshot to confirm.',
  'app.book.yourNameHelper': 'Shown to the business on your booking.',
  'app.book.passBodyVerifying': '{name} is checking your payment, usually within 2 hours. You’ll get an SMS when it’s confirmed.',
  'app.book.passBodyPending': '{name} will confirm your booking shortly. You’ll get an SMS when it’s confirmed.',
  'app.book.passBodyConfirmed': 'Your booking at {name} is confirmed. Show the QR when you arrive.',
  'app.book.passProofFailed': 'Your booking is placed, but the payment proof didn’t upload. The business can still see your booking.',
  'app.book.fService': 'Service',
  'app.book.fDueVenue': 'Due at the venue',
  'app.book.fAdvance': 'Advance',
  'app.book.fSerial': 'Your serial',
  'app.book.showArrive': 'Show this when you arrive',
  'app.book.nxVerify1': 'The business checks your payment.',
  'app.book.nxVerifyWallet': 'The business checks your {wallet} payment.',
  'app.book.nxPending1': 'The business confirms your booking.',
  'app.book.nxConfirmed1': 'Your booking is confirmed.',
  'app.book.nx2': 'You get an SMS and the pass turns green.',
  'app.book.nxArrive': 'Show the QR when you arrive.',
  'app.book.nxDue': 'Show the QR when you arrive and pay {amount} there.',
  'app.book.nxPayNothing': 'Nothing more to pay.',
  'app.book.copied': 'Copied',
  'app.book.qrOpenA11y': 'Show large QR code',
  'app.book.qrCloseA11y': 'Close QR code',
  'app.book.qrHint': 'Hold the screen up to the scanner.',
  'app.book.shareMessage': '{name} · {when} · {code}',
  'app.book.calendarFailed': 'We couldn’t open the calendar file.',
  'app.book.passError': 'We couldn’t load this pass.',
  'app.book.passNotFound': 'We can’t find this pass.',
  'app.book.passOfflineNone': 'This pass isn’t saved on this phone yet. Reconnect to open it.',
  'app.book.perHour': '{amount} an hour',
  'app.book.hoursX': '{n, plural, one{# hour} other{# hours}} × {price}',
  'app.book.peakHoursX': '{n, plural, one{# peak hour} other{# peak hours}} × {price}',
  'app.book.exitBook': 'Back to services',
  'app.book.ownHold': 'Your earlier pick is still held for you until {time}. Pick it again, or wait and we’ll hold this one then.',
  'app.book.errMaybePlaced': 'This booking may already be placed. Check your bookings before you try again.',
  'app.book.passOfflineAgo': 'Offline · last updated {ago}',
  'app.book.walletTile.bkash': 'bKash',
  'app.book.walletTile.nagad': 'Nagad',
  'app.book.walletTile.rocket': 'Rocket',
  'app.book.walletTile.bank': 'Bank',
  'app.book.passClose': 'Close pass',
} as const satisfies Record<string, string>;

export const appBnBook = {
  'app.book.title': 'বুকিং',
  'app.book.closeA11y': 'বন্ধ করুন, বুকিং ছেড়ে যান',
  'app.book.optionalSuffix': '{label} (ঐচ্ছিক)',
  'app.book.decrease': '{label} কমান',
  'app.book.increase': '{label} বাড়ান',
  'app.book.num': '{n}',
  'app.book.backA11y': 'পেছনে',
  'app.book.chooseServices': 'সার্ভিস বেছে নিন',
  'app.book.maxPer': 'প্রতি বুকিংয়ে সর্বোচ্চ {n}টি',
  'app.book.attendeeName': 'যিনি যাবেন তাঁর নাম',
  'app.book.noteFor': '{name}-এর জন্য বুকিং',
  'app.book.pickStart': 'শুরুর তারিখ বেছে নিন',
  'app.book.pickRange': 'নেওয়া ও ফেরতের তারিখ বেছে নিন',
  'app.book.pickSessions': 'সেশন বেছে নিন',
  'app.book.peakLine': 'পিক {range} +{amount}',
  'app.book.hoursSummary': '{n} ঘণ্টা · {range}',
  'app.book.hoursNotBack': '{n} ঘণ্টা · পরপর নয়',
  'app.book.bookedOf': '{capacity}-এর {booked}টি বুক হয়েছে',
  'app.book.serialsLeft': '{n}টি সিরিয়াল বাকি',
  'app.book.serialNote': 'সারিতে আপনার জায়গা। বুকিং শেষ করার সময় আমরা এটি ধরে রাখি।',
  'app.book.nights': '{n} রাত',
  'app.book.days': '{n} দিন',
  'app.book.prevMonth': 'আগের মাস',
  'app.book.nextMonth': 'পরের মাস',
  'app.book.priceTimes': '{label} · {price} × {qty}',
  'app.book.nextFree': 'পরবর্তী ফ্রি: {date}',
  'app.book.noTimesAny': 'এখন কোনো সময় খোলা নেই। বুক করতে ব্যবসাটিকে ফোন করুন।',
  'app.book.timesError': 'সময়গুলো লোড করা যায়নি।',
  'app.book.pageError': 'এই ব্যবসাটি লোড করা যায়নি।',
  'app.book.notFound': 'এই ব্যবসাটি পাওয়া যায়নি।',
  'app.book.holdBusy': 'সময়টি ধরে রাখা হচ্ছে…',
  'app.book.discount': 'অফারের ছাড়',
  'app.book.payNowPct': 'এখন দিন ({pct}% অগ্রিম)',
  'app.book.otpSentTo': '{phone} নম্বরে পাঠানো ৬ সংখ্যার কোড দিন',
  'app.book.otpWrong': 'কোডটি মেলেনি। দেখে আবার চেষ্টা করুন।',
  'app.book.otpLocked': 'অনেকবার ভুল হয়েছে। ১০ মিনিট পরে আবার চেষ্টা করুন।',
  'app.book.otpRateLimited': 'অনেকগুলো কোড পাঠানো হয়েছে। কিছুক্ষণ পরে আবার চেষ্টা করুন।',
  'app.book.otpSendFailed': 'কোড পাঠানো যায়নি। আবার চেষ্টা করুন।',
  'app.book.otpVerifyFailed': 'কোড যাচাই করা যায়নি। আবার চেষ্টা করুন।',
  'app.book.resendCode': 'আবার কোড পাঠান',
  'app.book.phoneA11y': 'ফোন নম্বর',
  'app.book.payDirectSub': '{wallets} · ব্যবসা যাচাই করে',
  'app.book.listOr': 'বা',
  'app.book.payVenueSub': 'পুরো {amount} ভেন্যুতে',
  'app.book.payVenueSubRisk': 'পুরো {amount} ভেন্যুতে · স্লট নিশ্চিত নয়',
  'app.book.payVenueFree': 'এখন কিছু দিতে হবে না · ভেন্যুতে দিন',
  'app.book.wallet.bkash': 'বিকাশ',
  'app.book.wallet.nagad': 'নগদ',
  'app.book.wallet.rocket': 'রকেট',
  'app.book.wallet.bank': 'ব্যাংক ট্রান্সফার',
  'app.book.sendAmt': '{amount} পাঠান ({pct}% অগ্রিম)',
  'app.book.sendAmtPlain': '{amount} পাঠান',
  'app.book.stepOpenWallet': '{wallet} খুলুন → সেন্ড মানি',
  'app.book.stepOpenBank': 'আপনার ব্যাংকিং অ্যাপ খুলুন → ট্রান্সফার',
  'app.book.stepSend': '{number} নম্বরে {amount} পাঠান',
  'app.book.openWallet': '{wallet} খুলুন',
  'app.book.openWalletFailed': '{wallet} খোলা যায়নি। নিজে অ্যাপ খুলে টাকা পাঠান।',
  'app.book.maskedNote': 'নিশ্চিত করার পরেই পুরো নম্বর দেখানো হবে। তখন টাকা পাঠিয়ে পাস থেকে পেমেন্ট প্রুফ যোগ করুন।',
  'app.book.passSendTo': '{number} নম্বরে {amount} পাঠান, তারপর পেমেন্ট প্রুফ পাঠান যাতে ব্যবসা নিশ্চিত করতে পারে।',
  'app.book.passSendProof': 'ব্যবসাকে {amount} পাঠান, তারপর পেমেন্ট প্রুফ পাঠান যাতে তারা নিশ্চিত করতে পারে।',
  'app.book.helper': 'TrxID বা স্ক্রিনশট যথেষ্ট। ব্যবসা ২ ঘণ্টার মধ্যে যাচাই করে।',
  'app.book.shotAdded': 'স্ক্রিনশট যোগ হয়েছে',
  'app.book.removeShot': 'সরান',
  'app.book.shotTitle': 'স্ক্রিনশট দিন',
  'app.book.camera': 'ছবি তুলুন',
  'app.book.gallery': 'গ্যালারি থেকে বেছে নিন',
  'app.book.shotDenied': 'স্ক্রিনশট দিতে সেটিংসে ছবির অনুমতি দিন।',
  'app.book.shotFailed': 'ছবিটি পড়া যায়নি। অন্য ছবি দিন।',
  'app.book.confirming': 'নিশ্চিত হচ্ছে…',
  'app.book.callToBookT': 'ফোন করে বুক করুন',
  'app.book.callToBookB': 'এই ব্যবসা অনলাইনে পেমেন্টের তথ্য নিতে পারছে না। বুক করতে ফোন বা মেসেজ করুন।',
  'app.book.errGeneric': 'কিছু একটা ভুল হয়েছে। আবার চেষ্টা করুন।',
  'app.book.errOffline': 'আপনি অফলাইনে। এগোতে ইন্টারনেট ফিরিয়ে আনুন।',
  'app.book.errAuth': 'আবার ফোন যাচাই করুন।',
  'app.book.errStale': 'সার্ভিসটি বদলেছে। পেছনে গিয়ে তথ্য দেখে নিন।',
  'app.book.hintPhone': 'নিশ্চিত করতে ফোন যাচাই করুন।',
  'app.book.hintName': 'নিশ্চিত করতে আপনার নাম দিন।',
  'app.book.hintPay': 'কীভাবে পেমেন্ট করবেন বেছে নিন।',
  'app.book.hintProof': 'নিশ্চিত করতে TrxID বা স্ক্রিনশট দিন।',
  'app.book.yourNameHelper': 'আপনার বুকিংয়ে ব্যবসাকে এটি দেখানো হয়।',
  'app.book.passBodyVerifying': '{name} আপনার পেমেন্ট যাচাই করছে, সাধারণত ২ ঘণ্টার মধ্যে। নিশ্চিত হলে এসএমএস পাবেন।',
  'app.book.passBodyPending': '{name} শীঘ্রই আপনার বুকিং নিশ্চিত করবে। নিশ্চিত হলে এসএমএস পাবেন।',
  'app.book.passBodyConfirmed': '{name}-এ আপনার বুকিং নিশ্চিত। পৌঁছে QR দেখান।',
  'app.book.passProofFailed': 'আপনার বুকিং হয়েছে, কিন্তু পেমেন্ট প্রুফ আপলোড হয়নি। ব্যবসা আপনার বুকিং দেখতে পাচ্ছে।',
  'app.book.fService': 'সার্ভিস',
  'app.book.fDueVenue': 'ভেন্যুতে বাকি',
  'app.book.fAdvance': 'অগ্রিম',
  'app.book.fSerial': 'আপনার সিরিয়াল',
  'app.book.showArrive': 'পৌঁছে এটি দেখান',
  'app.book.nxVerify1': 'ব্যবসা আপনার পেমেন্ট যাচাই করে।',
  'app.book.nxVerifyWallet': 'ব্যবসা আপনার {wallet} পেমেন্ট যাচাই করে।',
  'app.book.nxPending1': 'ব্যবসা আপনার বুকিং নিশ্চিত করে।',
  'app.book.nxConfirmed1': 'আপনার বুকিং নিশ্চিত।',
  'app.book.nx2': 'আপনি এসএমএস পান এবং পাস সবুজ হয়ে যায়।',
  'app.book.nxArrive': 'পৌঁছে QR দেখান।',
  'app.book.nxDue': 'পৌঁছে QR দেখান এবং সেখানে {amount} দিন।',
  'app.book.nxPayNothing': 'আর কিছু দিতে হবে না।',
  'app.book.copied': 'কপি হয়েছে',
  'app.book.qrOpenA11y': 'বড় QR কোড দেখান',
  'app.book.qrCloseA11y': 'QR কোড বন্ধ করুন',
  'app.book.qrHint': 'স্ক্যানারের সামনে স্ক্রিন ধরুন।',
  'app.book.shareMessage': '{name} · {when} · {code}',
  'app.book.calendarFailed': 'ক্যালেন্ডার ফাইল খোলা যায়নি।',
  'app.book.passError': 'এই পাস লোড করা যায়নি।',
  'app.book.passNotFound': 'এই পাস পাওয়া যায়নি।',
  'app.book.passOfflineNone': 'এই পাস এখনও এই ফোনে সেভ হয়নি। খুলতে ইন্টারনেট ফিরিয়ে আনুন।',
  'app.book.perHour': 'ঘণ্টায় {amount}',
  'app.book.hoursX': '{n} ঘণ্টা × {price}',
  'app.book.peakHoursX': '{n} পিক ঘণ্টা × {price}',
  'app.book.exitBook': 'সেবায় ফিরুন',
  'app.book.ownHold': 'আপনার আগের বাছাই {time} পর্যন্ত আপনার জন্য ধরে রাখা আছে। সেটি আবার বাছুন, অথবা অপেক্ষা করুন — তখন এটি ধরে রাখব।',
  'app.book.errMaybePlaced': 'বুকিংটি হয়তো ইতিমধ্যে হয়ে গেছে। আবার চেষ্টার আগে আপনার বুকিং দেখে নিন।',
  'app.book.passOfflineAgo': 'অফলাইন · শেষ আপডেট {ago}',
  'app.book.walletTile.bkash': 'bKash',
  'app.book.walletTile.nagad': 'Nagad',
  'app.book.walletTile.rocket': 'Rocket',
  'app.book.walletTile.bank': 'ব্যাংক',
  'app.book.passClose': 'পাস বন্ধ করুন',
} as const satisfies Record<string, string>;
